import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ClientHeader } from '../components/client/ClientHeader';
import { ConsultationCard, LoadingCard, EmptyState, STATUS_COLOR, money, fmtDateTime } from '../components/client/clientShared';
import {
  CalendarClock, CheckCircle2, XCircle, MessageSquare, Clock, MapPin, PawPrint,
  Filter, ChevronRight, Receipt, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELED', 'NO_SHOW'];

export default function ClientBookingsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [filters, setFilters] = useState({ providerId: '', animalId: '', status: '', dateFrom: '', dateTo: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [p, a] = await Promise.all([
          axios.get(`${API_URL}/api/client/providers`, { headers }),
          axios.get(`${API_URL}/api/client/animals`, { headers }),
        ]);
        setProviders(p.data || []);
        setAnimals(a.data || []);
      } catch { /* non-blocking */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'consultations') {
        const c = await axios.get(`${API_URL}/api/client/consultations`, { headers });
        setConsultations(c.data || []);
      } else {
        const params = new URLSearchParams({ scope: tab });
        if (filters.providerId) params.set('providerId', filters.providerId);
        if (filters.animalId) params.set('animalId', filters.animalId);
        if (filters.status) params.set('status', filters.status);
        if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.set('dateTo', filters.dateTo);
        const res = await axios.get(`${API_URL}/api/client/bookings?${params.toString()}`, { headers });
        setBookings(res.data || []);
      }
    } catch (err) {
      toast.error('Could not load your bookings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const hasActiveFilters = filters.providerId || filters.animalId || filters.status || filters.dateFrom || filters.dateTo;
  const clearFilters = () => setFilters({ providerId: '', animalId: '', status: '', dateFrom: '', dateTo: '' });
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === '__all__' ? '' : v }));

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader title="My Bookings" subtitle="Appointments & consultations" backTo="/client/dashboard" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming"><CalendarClock className="h-4 w-4 mr-1 hidden sm:inline" /> Upcoming</TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past"><CheckCircle2 className="h-4 w-4 mr-1 hidden sm:inline" /> Past</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="tab-cancelled"><XCircle className="h-4 w-4 mr-1 hidden sm:inline" /> Cancelled</TabsTrigger>
            <TabsTrigger value="consultations" data-testid="tab-consultations"><MessageSquare className="h-4 w-4 mr-1 hidden sm:inline" /> Consults</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab !== 'consultations' && (
          <Card data-testid="booking-filters">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" /> Filters
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="h-3 w-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                <Select value={filters.providerId || '__all__'} onValueChange={(v) => setF('providerId', v)}>
                  <SelectTrigger data-testid="filter-provider"><SelectValue placeholder="Provider" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All providers</SelectItem>
                    {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.displayName}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.animalId || '__all__'} onValueChange={(v) => setF('animalId', v)}>
                  <SelectTrigger data-testid="filter-animal"><SelectValue placeholder="Animal" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All animals</SelectItem>
                    {animals.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.status || '__all__'} onValueChange={(v) => setF('status', v)}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Any status</SelectItem>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={filters.dateFrom} onChange={(e) => setF('dateFrom', e.target.value)} data-testid="filter-date-from" aria-label="From date" />
                <Input type="date" value={filters.dateTo} onChange={(e) => setF('dateTo', e.target.value)} data-testid="filter-date-to" aria-label="To date" />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {loading ? (
            <LoadingCard />
          ) : tab === 'consultations' ? (
            consultations.length === 0 ? (
              <EmptyState icon={<MessageSquare className="h-10 w-10 opacity-40" />} title="No consultations yet" cta="Browse Providers" onCta={() => navigate('/')} />
            ) : consultations.map((c) => <ConsultationCard key={c.id} c={c} navigate={navigate} />)
          ) : bookings.length === 0 ? (
            <EmptyState
              icon={tab === 'cancelled' ? <XCircle className="h-10 w-10 opacity-40" /> : <CalendarClock className="h-10 w-10 opacity-40" />}
              title={hasActiveFilters ? 'No bookings match your filters' : tab === 'upcoming' ? 'No upcoming appointments' : tab === 'cancelled' ? 'No cancelled bookings' : 'No past appointments yet'}
              cta={tab === 'upcoming' && !hasActiveFilters ? 'Find a Provider' : undefined}
              onCta={() => navigate('/')}
            />
          ) : (
            bookings.map((b) => <ClientBookingRow key={b.id} b={b} onOpen={() => navigate(`/client/bookings/${b.id}`)} />)
          )}
        </div>
      </main>
    </div>
  );
}

function ClientBookingRow({ b, onOpen }) {
  const pay = b.invoice?.status || b.paymentStatus;
  return (
    <Card data-testid={`booking-${b.id}`} className="hover:border-primary transition-colors cursor-pointer" onClick={onOpen}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'}>{b.status}</Badge>
              {b.serviceName && <span className="font-semibold">{b.serviceName}</span>}
              {pay && <Badge variant="outline" className={STATUS_COLOR[pay] || ''}>{pay}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" /> {fmtDateTime(b.startDateTime)}
            </p>
            {b.providerName && <p className="text-sm mt-1">with {b.providerName}</p>}
            {(b.locationAddress || b.jobAddress || b.jobLocation) && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {b.locationAddress || b.jobAddress || b.jobLocation}
              </p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
              {b.totalCents != null && <span className="font-medium text-foreground">{money(b.totalCents)}</span>}
              {b.animals?.length > 0 && <span className="flex items-center gap-1"><PawPrint className="h-3 w-3" /> {b.animals.map((a) => a.name).join(', ')}</span>}
              {b.clientPhotosCount > 0 && <span className="flex items-center gap-1"><Receipt className="h-3 w-3" /> {b.clientPhotosCount} photo{b.clientPhotosCount !== 1 ? 's' : ''}</span>}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}
